import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Plus, MessageSquare, Pin, Lock, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CourseForum = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTopicDialog, setShowNewTopicDialog] = useState(false);
  const [newTopic, setNewTopic] = useState({ title: '', content: '' });
  const [submitting, setSubmitting] = useState(false);

  const canModerate = user?.role === 'Administrador' || user?.role === 'Formador';

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const fetchData = async () => {
    try {
      const [courseRes, topicsRes] = await Promise.all([
        axios.get(`${API_URL}/online-courses/${courseId}`),
        axios.get(`${API_URL}/forums/course/${courseId}/topics`)
      ]);
      setCourse(courseRes.data);
      setTopics(topicsRes.data);
    } catch (error) {
      toast.error('Error al cargar el foro');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.title.trim() || !newTopic.content.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/forums/topics`, {
        online_course_id: courseId,
        title: newTopic.title,
        content: newTopic.content
      });
      toast.success('Tema creado correctamente');
      setShowNewTopicDialog(false);
      setNewTopic({ title: '', content: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el tema');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePin = async (topicId) => {
    try {
      await axios.put(`${API_URL}/forums/topics/${topicId}/pin`);
      fetchData();
    } catch (error) {
      toast.error('Error al fijar el tema');
    }
  };

  const handleToggleClose = async (topicId) => {
    try {
      await axios.put(`${API_URL}/forums/topics/${topicId}/close`);
      fetchData();
    } catch (error) {
      toast.error('Error al cerrar el tema');
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este tema?')) return;
    
    try {
      await axios.delete(`${API_URL}/forums/topics/${topicId}`);
      toast.success('Tema eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el tema');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/online-courses/${courseId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al curso
          </Button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope font-bold text-3xl text-slate-900">
              Foro del Curso
            </h1>
            <p className="font-inter text-slate-600 mt-1">
              {course?.nombre}
            </p>
          </div>
          <Dialog open={showNewTopicDialog} onOpenChange={setShowNewTopicDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#004E89] hover:bg-[#003B66]">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Tema
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Nuevo Tema</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTopic} className="space-y-4">
                <div>
                  <Label htmlFor="title">Título</Label>
                  <Input
                    id="title"
                    value={newTopic.title}
                    onChange={(e) => setNewTopic({ ...newTopic, title: e.target.value })}
                    placeholder="Escribe un título descriptivo"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="content">Contenido</Label>
                  <Textarea
                    id="content"
                    value={newTopic.content}
                    onChange={(e) => setNewTopic({ ...newTopic, content: e.target.value })}
                    placeholder="Describe tu pregunta o tema de discusión..."
                    rows={5}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowNewTopicDialog(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#004E89] hover:bg-[#003B66]" disabled={submitting}>
                    {submitting ? 'Creando...' : 'Crear Tema'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Topics List */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          {topics.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No hay temas en este foro todavía.</p>
              <p className="text-sm mt-1">¡Sé el primero en iniciar una discusión!</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {topics.map((topic) => (
                <div
                  key={topic.id}
                  className={`p-4 hover:bg-slate-50 transition-colors ${topic.is_pinned ? 'bg-blue-50/50' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 cursor-pointer" onClick={() => navigate(`/forums/topics/${topic.id}`)}>
                      <div className="flex items-center gap-2 mb-1">
                        {topic.is_pinned && (
                          <Pin className="w-4 h-4 text-blue-600" />
                        )}
                        {topic.is_closed && (
                          <Lock className="w-4 h-4 text-slate-400" />
                        )}
                        <h3 className="font-semibold text-slate-900 hover:text-[#004E89]">
                          {topic.title}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                        {topic.content}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>Por {topic.user_name}</span>
                        <span>•</span>
                        <span>{formatDate(topic.created_at)}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          {topic.replies_count} respuestas
                        </span>
                      </div>
                    </div>
                    {(canModerate || topic.user_id === user?.id) && (
                      <div className="flex items-center gap-1">
                        {canModerate && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleTogglePin(topic.id); }}
                              title={topic.is_pinned ? 'Desfijar' : 'Fijar'}
                            >
                              <Pin className={`w-4 h-4 ${topic.is_pinned ? 'text-blue-600' : 'text-slate-400'}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleToggleClose(topic.id); }}
                              title={topic.is_closed ? 'Abrir' : 'Cerrar'}
                            >
                              <Lock className={`w-4 h-4 ${topic.is_closed ? 'text-orange-600' : 'text-slate-400'}`} />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteTopic(topic.id); }}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CourseForum;
